
  import { computed, defineComponent } from 'vue';
  import { ReportFilter } from '@/services/api/resources/analytics-builder/model';
  import { useFilterDateDropdown } from '@/ui/concerns/analytics/useFilterDateDropdown';
  import { d } from '@/ui/i18n/i18n';
  import { ReportFilterDateRange } from '@/services/api/resources/analytics-builder/constants';
  import { recreateDateFromUTC } from '@/ui/helpers';
  import { SelectOptions } from '@/ui/types';
  import { approvalStatusToText } from '@/ui/dictionary';

  interface GuestFiltersProps {
    filters: ReportFilter;
    folderOptions: SelectOptions;
    entityOptions: SelectOptions;
    groupOptions: SelectOptions;
    userOptions: SelectOptions;
    categoryOptions: SelectOptions;
    checklistOptions: SelectOptions;
    punctualityOptions: SelectOptions<number | null>;
    isSectionVisible: boolean;
    sectionOptions: SelectOptions<string>;
    isFormItemVisible: boolean;
    formItemOptions: SelectOptions<string>;
    isPriorityVisible: boolean;
    priorityOptions: SelectOptions<number | null>;
    isFormStatusVisible: boolean;
    formStatusOptions: SelectOptions<number | null>;
    isActionStatusVisible: boolean;
    actionStatusOptions: SelectOptions<number | null>;
    isCreatorVisible: boolean;
    approvalStatusOptions: SelectOptions;
    isApprovalStatusVisible: boolean;
    isApproversVisible: boolean;
  }

  export default defineComponent<GuestFiltersProps>({
    props: ['filters', 'folderOptions', 'entityOptions', 'groupOptions', 'userOptions', 'categoryOptions',
            'checklistOptions', 'punctualityOptions', 'isSectionVisible', 'sectionOptions', 'isFormItemVisible',
            'formItemOptions', 'isPriorityVisible', 'priorityOptions', 'isFormStatusVisible', 'formStatusOptions',
            'isActionStatusVisible', 'actionStatusOptions', 'isCreatorVisible', 'approvalStatusOptions',
            'isApprovalStatusVisible', 'isApproversVisible'],
    setup(props) {
      const { findRangeTitle } = useFilterDateDropdown();

      const getCustomDateText = (dateStart: Date, dateEnd: Date) => {
        const start = d(recreateDateFromUTC(dateStart), 'shortDateNumeric');
        const end = d(recreateDateFromUTC(dateEnd), 'shortDateNumeric');

        return `${start} - ${end}`;
      };

      const dateText = computed(() => {
        if (!props.filters.date.range) {
          return null;
        }

        const { dateStart, dateEnd } = props.filters.date;

        return props.filters.date.range === ReportFilterDateRange.custom && dateStart && dateEnd
          ? getCustomDateText(dateStart, dateEnd)
          : findRangeTitle(props.filters.date.range);
      });

      const approvalDateText = computed(() => {
        if (!props.filters.approvalDate?.range) {
          return null;
        }

        const { dateStart, dateEnd } = props.filters.approvalDate;

        return props.filters.approvalDate.range === ReportFilterDateRange.custom && dateStart && dateEnd
          ? getCustomDateText(dateStart, dateEnd)
          : findRangeTitle(props.filters.approvalDate.range);
      });

      const getLabels = (
        values: number[] | string[],
        options: SelectOptions<number | string | null>,
      ) => options.filter(option => values.includes(option.value as never))
        .map(option => option.label)
        .join(', ');

      const folders = computed(() => getLabels(props.filters.folder.values, props.folderOptions));
      const entities = computed(() => getLabels(props.filters.entity.values, props.entityOptions));
      const groups = computed(() => getLabels(props.filters.group.values, props.groupOptions));
      const users = computed(() => getLabels(props.filters.user.values, props.userOptions));
      const categories = computed(() => getLabels(props.filters.category.values, props.categoryOptions));
      const checklists = computed(() => getLabels(props.filters.checklist.values, props.checklistOptions));
      const punctuality = computed(() => props.punctualityOptions
        .find(option => option.value === props.filters.punctuality)?.label);
      const sections = computed(() => getLabels(props.filters.section?.values ?? [], props.sectionOptions));
      const formItem = computed(() => props.formItemOptions
        .find(option => option.value === props.filters.formItem)?.label);
      const priority = computed(() => props.priorityOptions
        .find(option => option.value === props.filters.priority)?.label);
      const formStatus = computed(() => getLabels(props.filters.formStatus?.values ?? [], props.formStatusOptions));
      const actionStatus = computed(() =>
        getLabels(props.filters.actionStatus?.values ?? [], props.actionStatusOptions));
      const creator = computed(() => getLabels(props.filters.creator?.values ?? [], props.userOptions));
      const approvalStatus = computed(() => props.approvalStatusOptions
        .filter(({ value }) => props.filters.approvalStatus?.values.includes(value))
        .map(option => approvalStatusToText(option.label as never))
        .join(', '));
      const approvers = computed(() => getLabels(props.filters.approvers?.values ?? [], props.userOptions));

      return {
        dateText,
        approvalDateText,
        folders,
        entities,
        groups,
        users,
        categories,
        checklists,
        punctuality,
        sections,
        formItem,
        priority,
        formStatus,
        actionStatus,
        creator,
        approvalStatus,
        approvers,
      };
    },
  });
